import React, { useState, useEffect, useMemo } from "react";
import {
  Modal,
  Stack,
  Text,
  Button,
  Group,
  List,
  ScrollArea,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconCheck, IconAlertTriangle, IconTag } from "@tabler/icons-react";
import ChipSelector from "@components/common/ChipSelector/ChipSelector";
import type { ChipOption } from "@components/common/ChipSelector/ChipSelector";
import { useTagStore } from "@utils/stores/useTagStore";
import { addClientTags } from "@utils/api/tags";

interface ManageClientTagsModalProps {
  opened: boolean;
  onClose: () => void;
  selectedClientIds: string[];
  selectedClientNames: string[];
  onUpdateComplete: () => void;
}

/**
 * ManageClientTagsModal component for bulk adding tags to selected clients
 * Allows users to select tags and apply them to multiple clients at once
 */
const ManageClientTagsModal: React.FC<ManageClientTagsModalProps> = ({
  opened,
  onClose,
  selectedClientIds,
  selectedClientNames,
  onUpdateComplete,
}) => {
  const { businessTags, loading: tagsLoading, initialize } = useTagStore();
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [isUpdating, setIsUpdating] = useState(false);

  // Initialize tags when modal opens
  useEffect(() => {
    if (opened && !tagsLoading) {
      initialize();
    }
  }, [opened, initialize, tagsLoading]);

  // Convert tags to ChipOption format
  const tagOptions: ChipOption[] = useMemo(() => {
    return businessTags.map((tag) => ({
      id: tag.id,
      name: tag.name,
      color: tag.color,
    }));
  }, [businessTags]);

  // Handle chip selection change
  const handleSelectionChange = (tagId: string, isSelected: boolean) => {
    setSelectedTagIds((prev) => {
      if (isSelected) {
        return [...prev, tagId];
      } else {
        return prev.filter((id) => id !== tagId);
      }
    });
  };

  // Handle bulk tag addition
  const handleAddTags = async () => {
    if (selectedTagIds.length === 0) {
      notifications.show({
        title: "No Tags Selected",
        message: "Please select at least one tag to add.",
        color: "orange",
        icon: <IconAlertTriangle size={18} />,
      });
      return;
    }

    if (selectedClientIds.length === 0) {
      notifications.show({
        title: "No Clients Selected",
        message: "No clients are selected. Please select clients first.",
        color: "red",
        icon: <IconAlertTriangle size={18} />,
      });
      return;
    }

    setIsUpdating(true);

    try {
      // Add tags to each client
      const updatePromises = selectedClientIds.map((clientId) =>
        addClientTags(clientId, selectedTagIds)
      );

      await Promise.all(updatePromises);

      // Find selected tag names for the notification
      const selectedTagNames = businessTags
        .filter((tag) => selectedTagIds.includes(tag.id))
        .map((tag) => tag.name)
        .join(", ");

      notifications.show({
        title: "Success",
        message: `Added tags (${selectedTagNames}) to ${selectedClientIds.length} client(s)`,
        color: "green",
        icon: <IconCheck size={18} />,
      });

      // Reset and close
      setSelectedTagIds([]);
      onClose();
      onUpdateComplete();
    } catch (error) {
      console.error("Failed to add tags to clients:", error);
      notifications.show({
        title: "Error",
        message: "Failed to add tags. Please try again.",
        color: "red",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle modal close
  const handleClose = () => {
    if (!isUpdating) {
      setSelectedTagIds([]);
      onClose();
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group gap="sm">
          <IconTag size={20} color="var(--mantine-color-blue-6)" />
          <Text fw={600}>Manage Tags</Text>
        </Group>
      }
      centered
      size="md"
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Add tags to{" "}
          <Text component="span" fw={600} c="dark">
            {selectedClientIds.length} client
            {selectedClientIds.length !== 1 ? "s" : ""}
          </Text>
          :
        </Text>

        {/* List of client names */}
        <ScrollArea.Autosize mah={150} type="auto">
          <List size="sm" spacing="xs">
            {selectedClientNames.map((name, index) => (
              <List.Item key={index}>
                <Text size="sm" fw={500}>
                  {name}
                </Text>
              </List.Item>
            ))}
          </List>
        </ScrollArea.Autosize>

        {/* Tag selector */}
        <ChipSelector
          title="Select Tags"
          options={tagOptions}
          selectedIds={selectedTagIds}
          onSelectionChange={handleSelectionChange}
          multiSelect={true}
          loading={tagsLoading}
          disabled={isUpdating}
          variant="colorful"
          size="sm"
        />

        <Text size="xs" c="dimmed" mt="xs">
          Note: Tags will be added to the selected clients. Existing tags will
          not be removed.
        </Text>

        {/* Action buttons */}
        <Group justify="flex-end" gap="sm" mt="md">
          <Button variant="subtle" onClick={handleClose} disabled={isUpdating}>
            Cancel
          </Button>
          <Button
            onClick={handleAddTags}
            loading={isUpdating}
            leftSection={<IconTag size={16} />}
            disabled={selectedTagIds.length === 0}
          >
            Add Tags to {selectedClientIds.length} Client
            {selectedClientIds.length !== 1 ? "s" : ""}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default ManageClientTagsModal;
